/**
 * The membership + invitation refusals (`{ error, code }` from
 * `/api/v1/organizations/*` and `/api/v1/invitations/*`, security package B)
 * turned into translated toasts — one place for every hook that writes a
 * membership or an invitation (`useOrganizationMembers`,
 * `useOrganizationInvitations`, `useAcceptInvitation`, the star toggle's
 * `MEMBERSHIP_MUTATION_META` users).
 *
 * A coded refusal gets its own sentence (`team.toasts.<code>`); anything else
 * the caller's fallback key, with the server message as the description.
 * Precedents: `fundErrorMessage` (useFunds.ts), `toastVolunteerError`
 * (useVolunteerSignup.ts).
 */
import { ORG_MEMBERSHIP_ERROR_CODES, type OrgMembershipErrorCode } from '@releve/shared';
import type { EnvelopeError } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

/** One toast per refusal code (literal keys, so the locale check sees them). */
const CODED_TOAST_KEYS: Record<OrgMembershipErrorCode, string> = {
  [ORG_MEMBERSHIP_ERROR_CODES.lastAdmin]: 'team.toasts.lastAdmin',
  [ORG_MEMBERSHIP_ERROR_CODES.selfChange]: 'team.toasts.selfChange',
  [ORG_MEMBERSHIP_ERROR_CODES.adminRequired]: 'team.toasts.adminRequired',
  [ORG_MEMBERSHIP_ERROR_CODES.memberNotFound]: 'team.toasts.memberNotFound',
  [ORG_MEMBERSHIP_ERROR_CODES.invitationNotFound]: 'team.toasts.invitationNotFound',
  [ORG_MEMBERSHIP_ERROR_CODES.invitationInvalid]: 'team.toasts.invitationInvalid',
  [ORG_MEMBERSHIP_ERROR_CODES.alreadyMember]: 'team.toasts.alreadyMember',
};

/**
 * Refusals are membership rules, not bugs: App.tsx's MutationCache.onError
 * skips Sentry for them. Spread as the `meta` of every membership mutation.
 */
export const MEMBERSHIP_MUTATION_META = {
  expectedErrorCodes: Object.values(ORG_MEMBERSHIP_ERROR_CODES) as readonly string[],
} as const;

/** The refusal code a failed membership request carries, or `null`. */
export function membershipErrorCodeOf(error: unknown): OrgMembershipErrorCode | null {
  const code = (error as EnvelopeError<{ code?: string }> | null | undefined)?.envelope?.code;
  return code && Object.prototype.hasOwnProperty.call(CODED_TOAST_KEYS, code)
    ? (code as OrgMembershipErrorCode)
    : null;
}

/**
 * The toast for a failed membership request: a coded refusal's own sentence,
 * else `fallbackKey` with the server message as the description. `params`
 * fills the coded sentence (`alreadyMember` names `{{email}}`).
 */
export function membershipErrorMessage(
  error: unknown,
  fallbackKey: string,
  params?: Record<string, unknown>,
): { message: string; description?: string } {
  const code = membershipErrorCodeOf(error);
  if (code) return { message: i18n.t(CODED_TOAST_KEYS[code], params) };
  const serverMessage = error instanceof Error ? error.message : '';
  return serverMessage
    ? { message: i18n.t(fallbackKey), description: serverMessage }
    : { message: i18n.t(fallbackKey) };
}

/** `toast.error` of {@link membershipErrorMessage}. */
export function toastMembershipError(
  error: unknown,
  fallbackKey: string,
  params?: Record<string, unknown>,
): void {
  const { message, description } = membershipErrorMessage(error, fallbackKey, params);
  toast.error(message, description ? { description } : undefined);
}
